import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import GroupPicker, { IGroupPickerProps } from 'components/domain/pickers/GroupPicker/GroupPicker';
import { DEFAULT_PICKER_PAGE_SIZE, findOrFetchPickerItem } from 'components/foundation/pickers/utils';
import { useOpenable } from 'hooks';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { useReactQueryLazyLoading } from 'hooks/react-query/useReactQueryLazyLoading/useReactQueryLazyLoading';
import useScrollerRef from 'hooks/ui/useScrollerRef';
import { ClassName } from 'types/ClassName';
import { IProjectGroup } from '../../../../types/ProjectGroup';
import { executeFunctionsCurried } from 'utils/functionUtils';
import { useProjectGroupPickerData } from './hooks/useProjectGroupPickerData';

export interface IProjectGroupPickerProps
  extends Omit<IGroupPickerProps, 'data' | 'isLoading' | 'currentGroup' | 'dataError' | 'loadedAll'> {
  className?: ClassName;
  excludeIds?: IProjectGroup['id'][];
}

export default function ProjectGroupPicker({
  value,
  excludeIds,
  className,
  onOpen,
  onClose,
  ...pickerProps
}: IProjectGroupPickerProps) {
  const toggle = useOpenable();
  const { scroller, setScrollerRef } = useScrollerRef();
  const baseKey = useReactQueryBaseKey();

  const { fetchPage, fetchSelected } = useProjectGroupPickerData({ excludeIds });

  const {
    data,
    isLoading,
    isError: isDataError,
    loadedAll,
  } = useReactQueryLazyLoading(
    scroller,
    [...baseKey, 'project-group-picker', { excludeIds }],
    fetchPage,
    DEFAULT_PICKER_PAGE_SIZE,
    { enabled: toggle.isOpen }
  );

  const { data: selectedGroup } = useQuery({
    queryKey: [...baseKey, 'project-group-picker-by-id', value],
    queryFn: () => findOrFetchPickerItem(value!, data, fetchSelected),
    enabled: !!value,
  });

  const classes = classNames('project-picker', className);

  return (
    <GroupPicker
      currentGroup={selectedGroup?.groupName ?? null}
      className={classes}
      setScrollerRef={setScrollerRef}
      loadedAll={loadedAll}
      dataError={isDataError}
      data={data}
      isLoading={isLoading}
      value={value}
      onOpen={executeFunctionsCurried(toggle.open, onOpen)}
      onClose={executeFunctionsCurried(toggle.close, onClose)}
      {...pickerProps}
    />
  );
}
